'use client';

import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFieldArray, useFormContext, get } from 'react-hook-form';
import { GalleryAdd, Trash, Notepad2, AddCircle } from 'iconsax-react';
import classNames from 'classnames';
import TitleText from '@/components/atoms/TitleText';
import InfoText from '@/components/atoms/InfoText';
import FormField from '@/components/FormField/FormField';
import Label from '@/components/atoms/Label';
import Input from '@/components/atoms/Input';
import Button from '@/components/atoms/Button';
import { useGetCategoryAttributesQuery } from '../../../../../../store/services/lookups';
import {
  useUploadProductColorImageByAdminMutation,
} from '../../../../../../store/services/products';
import LoaderSpin from '@/components/atoms/LoaderSpin';
import { VariantsTable } from './VariantsTable';
import { computePermutations } from '../_hooks/useVariantPermutations';
import { useCurrencyPreference } from '../../../../../../hooks/useCurrencyPreference';


// interface UploadResponse {
//   payload: {
//     id: string;
//     blobFileUrl: string;
//     blobName?: string;
//     blobSize?: number;
//     extension?: string;
//   }[];
// }

interface CategoryAttributeDetail {
  id: string;
  nameEn: string;
  nameAr: string;
  name: string;
}

interface CategoryAttribute {
  id: string;
  nameEn: string;
  nameAr: string;
  name: string;
  isColor: boolean;
  categoryAttributeDetails: CategoryAttributeDetail[];
}

interface CategoryAttributesResponse {
  payload: CategoryAttribute[];
}

const SkuInventoryForm = () => {
  const { t: t, i18n } = useTranslation();
  const { t: tComponents } = useTranslation();
  const {
    control,
    watch,
    setValue,
    register,
    clearErrors,
    trigger,
    formState: { errors, submitCount },
  } = useFormContext();

  const { currencyCode, currencyPreference } = useCurrencyPreference();
  const isNotSar = currencyCode !== 'SAR';



  const productType = watch('productType') ?? 'cart';
  const minQty = watch('minQty');
  const applyVariants = watch('applyVariants') ?? false;
  const applyPricingTiers = watch('applyPricingTiers') ?? false;

  useEffect(() => {
    if (applyPricingTiers && productType === 'rfq') {
      trigger('pricingTiers');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minQty]);

  React.useEffect(() => {
    if (productType === 'cart') {
      setValue('applyPricingTiers', false);
      setValue('pricingTiers', []);
    }
  }, [productType, setValue]);

  const EXCHANGE_RATE = 1;
  const PREFERRED_CURRENCY = currencyCode || 'SAR';

  // ── Color Field Array ──────────────────────────────────────────
  const {
    fields: colorFields,
    append: appendColor,
    remove: removeColor,
  } = useFieldArray({
    control,
    name: 'colors',
  });

  // ── Price Tiers Field Array ────────────────────────────────────
  const {
    fields: tierFields,
    append: appendTier,
    remove: removeTier,
  } = useFieldArray({
    control,
    name: 'pricingTiers',
  });

  // Watch tiers to compute dynamic total prices
  const watchedTiers = watch('pricingTiers') ?? [];

  // Automatically append at least one pricing tier when enabled and empty
  React.useEffect(() => {
    if (applyPricingTiers && watchedTiers.length === 0) {
      appendTier(
        {
          minQty: minQty ?? undefined,
          maxQty: undefined,
          pricePerUnit: undefined,
          internalLabel: '',
        },
        { shouldFocus: false }
      );
    }
  }, [applyPricingTiers, watchedTiers.length, appendTier, minQty]);

  // Custom colors state
  const [customColors, setCustomColors] = React.useState<
    { id: string | null; name: string }[]
  >([]);
  const [customColorName, setCustomColorName] = React.useState('');

  const handleAddCustomColor = (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (!customColorName.trim()) return;
    const newColor = { id: null, name: customColorName.trim() };
    setCustomColors([...customColors, newColor]);
    setCustomColorName('');
  };

  // Ref array to programmatically trigger hidden file inputs for colors
  const fileInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const [uploadColorImage] = useUploadProductColorImageByAdminMutation();
  const uploadColorImageApi = async (formData: FormData) => {
    return await uploadColorImage(formData).unwrap();
  };

  // ── Fetch category attributes ──────────────────────────────────
  const categoryId = watch('categoryId');

  const { data: attributesData, isLoading: attributesLoading } = useGetCategoryAttributesQuery(
    { categoryId: categoryId ?? '' },
    { skip: !categoryId }
  );

  // Memoize to provide a stable reference when attributesData is null.
  const attributes = React.useMemo<CategoryAttribute[]>(
    () => (attributesData as CategoryAttributesResponse | undefined)?.payload ?? [],
    [attributesData]
  );
  const colorAttribute = attributes.find((attr: CategoryAttribute) => attr.isColor);
  const nonColorAttributes = attributes.filter((attr: CategoryAttribute) => !attr.isColor);

  // ── Memoize permutations ────────────────────────────────────────
  const watchedFormAttributes = watch('attributes') as
    | Record<string, string[]>
    | undefined;
  const watchedFormColors = watch('colors');

  const memoizedPermutations = React.useMemo(
    () =>
      computePermutations(
        attributes,
        watchedFormAttributes ?? {},
        watchedFormColors ?? []
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      attributes,
      JSON.stringify(watchedFormAttributes),
      JSON.stringify(watchedFormColors),
    ]
  );

  React.useEffect(() => {
    if (applyVariants && colorAttribute && (watchedFormColors ?? []).length === 0) {
      appendColor({ color: undefined, image: [] }, { shouldFocus: false });
    }
  }, [applyVariants, !!colorAttribute, (watchedFormColors ?? []).length, appendColor]);

  // Store colorAttributeId in form when available, otherwise clear it and colors.
  // IMPORTANT: Only clear colors after attributes have actually loaded — otherwise the
  // else branch fires before the API responds and wipes colors hydrated by methods.reset(),
  // which cascades into variant permutation key mismatches and resets stock/price to 0.
  React.useEffect(() => {
    if (colorAttribute?.id) {
      setValue('colorAttributeId', colorAttribute.id, { shouldDirty: true });
    } else if (!attributesLoading && attributesData) {
      setValue('colorAttributeId', undefined, { shouldDirty: true });
      setValue('colors', [], { shouldDirty: true, shouldValidate: true });
    }
  }, [colorAttribute?.id, setValue, attributesLoading, attributesData]);

  const handleFileChange = async (
    index: number,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      const currentColors = watch('colors') ?? [];

      const initialFileObj = {
        uid: String(Date.now()),
        name: file.name,
        url: previewUrl,
        originFileObj: file,
        status: 'uploading' as const,
        uploading: true,
      };

      currentColors[index] = {
        ...currentColors[index],
        image: [initialFileObj],
      };
      setValue('colors', [...currentColors], {
        shouldDirty: true,
        shouldValidate: true,
      });

      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await uploadColorImageApi(formData);
        if (response && response.payload && response.payload[0]) {
          const item = response.payload[0];
          const updatedColors = watch('colors') ?? [];
          updatedColors[index] = {
            ...updatedColors[index],
            image: [
              {
                uid: item.id,
                name: item.blobName || file.name,
                url: item.blobFileUrl,
                originFileObj: file,
                status: 'done' as const,
                uploading: false,
              },
            ],
          };
          setValue('colors', [...updatedColors], {
            shouldDirty: true,
            shouldValidate: true,
          });
          clearErrors(`colors.${index}.image`);
        } else {
          throw new Error('Invalid response');
        }
      } catch {
        const updatedColors = watch('colors') ?? [];
        updatedColors[index] = {
          ...updatedColors[index],
          image: [],
        };
        setValue('colors', [...updatedColors], {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    }
  };
  const pricingTiersOverlapError =
    (errors as Record<string, { message?: string }>)?.['pricingTiers.overlap']
      ?.message ||
    (errors?.pricingTiers as Record<string, { message?: string }>)?.overlap
      ?.message;

  return (
    <div
      id="skuInventory"
      className="bg-white dark:bg-dark-surface-primary p-4 md:p-6 rounded-2xl border border-light-input-border dark:border-dark-input-border shadow-sm mb-8 animate-fade-in"
      data-testid="sku-inventory-form"
    >
      {/* Header */}
      <div
        className="mb-6 flex gap-4 items-center"
        data-testid="sku-inventory-header"
      >
        <div
          className="p-3 bg-soft-background dark:bg-dark-button-bg-secondary rounded-xl text-neutral-900 dark:text-dark-text-primary flex items-center justify-center"
          data-testid="sku-inventory-icon-container"
        >
          <Notepad2 variant="Bold" size={24} />
        </div>
        <div data-testid="sku-inventory-header-text">
          <TitleText
            text={t('skuInventory')}
            size="lg"
            className="!font-bold text-neutral-900 dark:text-dark-text-primary leading-none tracking-normal"
          />
          <InfoText
            text={t('skuInventoryDesc')}
            size="xs"
            className="!font-normal !leading-none tracking-normal text-neutral-500 mt-1 dark:text-dark-text-secondary"
          />
        </div>
      </div>

      {/* Product Type */}
      <div
        className="mb-6 flex flex-col gap-2"
        data-testid="product-type-section"
      >
        <FormField
          type="radio"
          name="productType"
          id="productType"
          label={
            <TitleText
              text={t('productType')}
              size="base"
              className="!font-semibold !leading-none text-neutral-900 dark:text-dark-text-primary"
            />
          }
          options={[
            {
              label: (
                <TitleText
                  text={t('cartMethod')}
                  size="sm"
                  className="!font-normal"
                />
              ),
              value: 'cart',
            },
            {
              label: (
                <TitleText
                  text={t('rfqMethod')}
                  size="sm"
                  className="!font-normal"
                />
              ),
              value: 'rfq',
            },
          ]}
          className="flex gap-8 !font-semibold"
          data-testid="product-type-container"
        />
      </div>

      {/* ── RFQ Method: Pricing Tiers ────────────────────────────── */}
      {productType === 'rfq' && (
        <div
          key="rfq-method-container"
          className="space-y-6"
          data-testid="rfq-method-content"
        >
          <div
            className="flex items-center justify-between gap-4 py-2 border-b border-light-input-border dark:border-dark-input-border"
            data-testid="apply-pricing-tiers-row"
          >
            <div data-testid="apply-pricing-tiers-text">
              <TitleText
                text={t('applyPricingTiers')}
                size="base"
                className="!font-semibold !leading-none text-neutral-900 dark:text-dark-text-primary"
              />
              <InfoText
                text={t('applyVariantsDesc')}
                size="xs"
                className="text-neutral-500 dark:text-dark-text-secondary mt-0.5"
              />
            </div>
            <FormField
              type="toggle"
              name="applyPricingTiers"
              id="applyPricingTiers"
              data-testid="apply-pricing-tiers-toggle"
              onChange={(checked: unknown) => {
                const isChecked = Boolean(checked);
                if (isChecked) {
                  setValue('applyPricingTiers', true, { shouldDirty: true });
                  clearErrors('pricingTiers');
                } else {
                  setValue('applyPricingTiers', false, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                  setValue('pricingTiers', [], {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }
              }}
            />
          </div>

          {applyPricingTiers && (
            <div
              className="space-y-6 animate-fade-in"
              data-testid="pricing-tiers-container"
            >
              <div data-testid="pricing-tiers-header-wrapper">
                <TitleText
                  text={t('volumePricingTiers')}
                  size="base"
                  className="!font-bold text-neutral-900 dark:text-dark-text-primary"
                />
                {pricingTiersOverlapError && (
                  <InfoText
                    text={pricingTiersOverlapError as string}
                    size="xs"
                    className="!text-red-500 dark:!text-red-400 !font-medium mt-1"
                    data-testid="pricing-tiers-overlap-error"
                  />
                )}
              </div>

              {tierFields.length > 0 && (
                <div
                  data-testid="pricing-tier-header"
                  className="hidden md:flex items-start gap-4 px-2 mb-4"
                >
                  <div
                    data-testid="pricing-tier-qty-from-wrapper"
                    className="flex-[2]"
                  >
                    <Label
                      text={t('qtyRange')}
                      className="text-sm !font-semibold leading-none !text-light-text-primary dark:!text-dark-text-primary"
                    />
                  </div>
                  <div
                    data-testid="pricing-tier-qty-to-wrapper"
                    className="flex-1"
                  >
                    <Label
                      text={`${t('pricePerUnit')} (${tComponents('SAR')})`}
                      className="text-sm !font-semibold leading-none !text-light-text-primary dark:!text-dark-text-primary"
                    />
                  </div>
                  <div
                    data-testid="pricing-tier-price-wrapper"
                    className="flex-1"
                  >
                    <Label
                      text={t('internalLabel')}
                      className="text-sm !font-semibold leading-none !text-light-text-primary dark:!text-dark-text-primary"
                    />
                  </div>
                  <div
                    data-testid="pricing-tier-spacer"
                    className="w-[42px]"
                  ></div>
                </div>
              )}

              <div
                data-testid="pricing-tier-internal-label-wrapper"
                className="flex flex-col"
              >
                {tierFields.map((field, index) => {
                  const minQty = Number(watchedTiers[index]?.minQty || 0);
                  const pricePerUnit = Number(
                    watchedTiers[index]?.pricePerUnit || 0
                  );
                  const total = minQty * pricePerUnit;

                  const tierInputClasses =
                    'bg-white dark:bg-transparent placeholder:!font-normal [&_input::placeholder]:!font-normal placeholder:!text-light-text-placeholder dark:placeholder:!text-dark-text-placeholder [&_input::placeholder]:!text-light-text-placeholder dark:[&_input::placeholder]:!text-dark-text-placeholder';

                  return (
                    <div
                      key={field.id}
                      className="flex flex-col px-2 pb-4 bg-[#F9FAFB] dark:bg-dark-surface-secondary rounded-2xl animate-scale-in"
                      data-testid={`pricing-tier-card-${index}`}
                    >
                      <div
                        className="flex flex-col md:flex-row items-start gap-4"
                        data-testid={`pricing-tier-fields-${index}`}
                      >
                        {/* Qty Range */}
                        <div
                          data-testid={`pricing-tier-internal-label-field-wrapper-${index}`}
                          className="flex-[2] flex flex-col gap-2 w-full"
                        >
                          <Label
                            text={t('qtyRange')}
                            className="text-xs !font-semibold text-neutral-800 dark:text-dark-text-secondary md:hidden"
                            id={`qty-range-label-${index}`}
                          />
                          <div
                            className="flex items-start gap-3 w-full"
                            data-testid={`qty-range-inputs-${index}`}
                          >
                            <div className="flex-1 w-full">
                              <FormField
                                name={`pricingTiers.${index}.minQty`}
                                id={`pricingTiers-${index}-minQty`}
                                type="number"
                                max={999999999}
                                className={tierInputClasses}
                                placeholder={t('qtyFromPlaceholder')}
                                data-testid={`pricingTiers-${index}-minQty`}
                                value={watchedTiers[index]?.minQty ?? null}
                              />
                            </div>
                            <div className="flex-1 w-full">
                              <FormField
                                name={`pricingTiers.${index}.maxQty`}
                                id={`pricingTiers-${index}-maxQty`}
                                type="number"
                                max={999999999}
                                className={tierInputClasses}
                                placeholder={t('qtyToPlaceholder')}
                                data-testid={`pricingTiers-${index}-maxQty`}
                                value={watchedTiers[index]?.maxQty ?? null}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Price Per Unit */}
                        <div
                          data-testid={`pricing-tier-total-price-wrapper-${index}`}
                          className="w-full flex-1 flex flex-col"
                        >
                          <Label
                            text={`${t('pricePerUnit')} (${tComponents('SAR')})`}
                            className="text-xs font-semibold text-neutral-800 dark:text-dark-text-secondary md:hidden mb-2"
                            id={`price-unit-label-${index}`}
                          />
                          <FormField
                            name={`pricingTiers.${index}.pricePerUnit`}
                            id={`pricingTiers-${index}-pricePerUnit`}
                            type="number"
                            max={999999999}
                            className={tierInputClasses}
                            placeholder={t('setPrice')}
                            data-testid={`pricingTiers-${index}-pricePerUnit`}
                            value={watchedTiers[index]?.pricePerUnit ?? null}
                          />
                        </div>

                        {/* Internal Label */}
                        <div
                          data-testid={`pricing-tier-actions-wrapper-${index}`}
                          className="w-full flex-1 flex flex-col gap-2"
                        >
                          <Label
                            text={t('internalLabel')}
                            className="text-xs !font-semibold text-neutral-800 dark:text-dark-text-secondary md:hidden"
                            id={`internal-label-${index}`}
                          />
                          <FormField
                            name={`pricingTiers.${index}.internalLabel`}
                            id={`label-${index}`}
                            type="text"
                            className={tierInputClasses}
                            placeholder={t('setLabel')}
                            data-testid={`tier-label-${index}`}
                          />
                        </div>

                        {/* Delete */}
                        <div
                          className="flex justify-end w-[42px] shrink-0"
                          data-testid={`tier-delete-wrapper-${index}`}
                        >
                          <div
                            className="h-[46px] flex items-center justify-center"
                            data-testid={`tier-delete-container-${index}`}
                          >
                            <button
                              type="button"
                              onClick={() => removeTier(index)}
                              className="w-10 h-10 flex items-center justify-center bg-red-50 text-red-500 hover:bg-red-100 dark:bg-red-950/20 dark:hover:bg-red-900/40 rounded-full transition-all duration-200"
                              data-testid={`tier-delete-${index}`}
                            >
                              <Trash size={20} variant="Outline" />
                            </button>
                          </div>
                        </div>
                      </div>
                      <div
                        className="mt-2 w-full flex justify-center"
                        data-testid={`tier-total-${index}`}
                      >
                        <InfoText
                          text={
                            isNotSar
                              ? `${t('totalPriceLabel', {
                                price: total.toLocaleString(),
                                currency: tComponents('SAR'),
                              })} ≈ ${(total * EXCHANGE_RATE).toLocaleString(undefined, { maximumFractionDigits: 3 })} ${i18n.language === 'ar' ? currencyPreference?.nameAr : currencyCode}`
                              : t('totalPriceLabel', {
                                price: total.toLocaleString(),
                                currency: tComponents('SAR'),
                              })
                          }
                          size="xs"
                          className="!font-normal !text-[#F2994A] block text-center"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {tierFields.length < 5 && (
                <div
                  data-testid="add-tier-button-wrapper"
                  className="w-full flex justify-center mt-2"
                >
                  <button
                    type="button"
                    onClick={() =>
                      appendTier({
                        minQty: undefined,
                        maxQty: undefined,
                        pricePerUnit: undefined,
                        internalLabel: '',
                      })
                    }
                    className="flex items-center justify-center gap-2 text-sm !font-semibold !text-[#189F53] hover:text-[#137D40] transition-colors"
                    data-testid="add-tier-btn"
                  >
                    <AddCircle size={18} variant="Bold" />
                    {t('addTier')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Shared: Apply Variants + Dynamic Attribute Variants ────── */}
      {(productType === 'cart' || productType === 'rfq') && (
        <div className="space-y-6 mt-4" data-testid="apply-variants-section">
          {/* Apply Variants toggle */}
          <div
            className="flex items-start justify-between gap-4 py-2"
            data-testid="apply-variants-row"
          >
            <div data-testid="apply-variants-text" className="pt-1">
              <TitleText
                text={t('applyVariants')}
                size="base"
                className="!font-bold !leading-none mb-1 !text-neutral-900 dark:text-dark-text-primary"
              />
              <InfoText
                text={t('applyVariantsDesc')}
                size="xs"
                className="!font-normal !leading-none !text-neutral-900 dark:text-dark-text-secondary mt-1.5"
              />
              <InfoText
                text={t('zeroStockWarning')}
                size="sm"
                className="!font-medium !leading-none text-yellow-600 dark:text-yellow-500 mt-2 block"
              />
            </div>
            <div
              data-testid="base-stock-fields-wrapper"
              className="flex flex-col items-end gap-1"
            >
              <FormField
                type="toggle"
                name="applyVariants"
                id="applyVariants"
                disabled={!categoryId}
                onChange={(checked) => {
                  const isChecked = checked as boolean;
                  setValue('applyVariants', isChecked, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                  setValue('hasVariants', isChecked, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                  if (!isChecked) {
                    setValue('colors', [], {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                    setValue(
                      'attributes',
                      {},
                      { shouldValidate: true, shouldDirty: true }
                    );
                    setValue('variants', [], {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                    setCustomColors([]);
                  }
                }}
                data-testid="apply-variants-toggle"
              />
              {!categoryId && (
                <InfoText
                  text={t('chooseIndustryFirst')}
                  size="xs"
                  className="text-yellow-600 dark:text-yellow-500 !font-medium text-right max-w-[200px]"
                />
              )}
            </div>
          </div>

          {/* Variants — only shown when Apply Variants is on */}
          {applyVariants && (
            <div
              className="space-y-8 animate-fade-in"
              data-testid="variants-container"
            >
              {/* ── Non-color attributes: multi-select ─────────── */}
              {nonColorAttributes.map((attr) => (
                <div key={attr.id} data-testid={`attribute-section-${attr.id}`}>
                  <FormField
                    type="select"
                    name={`attributes.${attr.id}`}
                    id={`attribute-${attr.id}`}
                    label={attr.name}
                    mode="multiple"
                    placeholder={t('selectVariant', {
                      name: attr.name.toLowerCase(),
                    })}
                    disabled={attributesLoading}
                    loading={attributesLoading}
                    options={attr.categoryAttributeDetails.map((detail: CategoryAttributeDetail) => ({
                      label: detail.name,
                      value: detail.id,
                    }))}
                    labelClassName="!font-semibold"
                    data-testid={`attribute-select-${attr.id}`}
                  />
                </div>
              ))}

              {/* ── Color attribute: single-select rows + image ─── */}
              {colorAttribute && (
                <div
                  className="space-y-4"
                  data-testid="color-attribute-section"
                >
                  {colorFields.map((field, index) => {
                    const watchedColors = watch('colors') ?? [];
                    const imageUrl = watchedColors[index]?.image?.[0]?.url;
                    const isUploading =
                      watchedColors[index]?.image?.[0]?.uploading;
                    const imageFieldError = get(
                      errors,
                      `colors.${index}.image`
                    );
                    // z.array() errors from superRefine land on .root.message in RHF
                    const imageErrorMessageRaw: string | undefined =
                      imageFieldError?.message ??
                      imageFieldError?.root?.message;
                    const imageErrorMessage =
                      submitCount > 0 || imageFieldError?.type === 'manual'
                        ? imageErrorMessageRaw
                        : undefined;

                    return (
                      <div
                        key={field.id}
                        className="flex flex-col gap-1.5 animate-scale-in"
                        data-testid={`color-row-${index}`}
                      >
                        <Label
                          text={colorAttribute.name}
                          className="text-sm !font-semibold text-neutral-900 dark:text-dark-text-primary"
                          id={`color-label-${index}`}
                        />
                        <div
                          className="flex items-start gap-4"
                          data-testid={`color-row-fields-${index}`}
                        >
                          {/* Color select */}
                          <div
                            className="flex-1"
                            data-testid={`color-select-container-${index}`}
                          >
                            <FormField
                              type="select"
                              name={`colors.${index}.color`}
                              id={`colors-input-${index}`}
                              labelClassName="!font-semibold"
                              placeholder={t('selectColor')}
                              disabled={attributesLoading}
                              loading={attributesLoading}
                              options={[
                                ...colorAttribute.categoryAttributeDetails.map(
                                  (d) => ({
                                    label: d.name,
                                    value: JSON.stringify({
                                      id: d.id,
                                      name: d.name,
                                    }),
                                  })
                                ),
                                ...customColors.map((c) => ({
                                  label: c.name,
                                  value: JSON.stringify({
                                    id: c.id,
                                    name: c.name,
                                  }),
                                })),
                                ...(watchedColors[index]?.color?.id === null &&
                                  !customColors.some(
                                    (c) =>
                                      c.name === watchedColors[index].color.name
                                  )
                                  ? [
                                    {
                                      label: watchedColors[index].color.name,
                                      value: JSON.stringify({
                                        id: null,
                                        name: watchedColors[index].color.name,
                                      }),
                                    },
                                  ]
                                  : []),
                              ]}
                              dropdownRender={(menu: React.ReactNode) => (
                                <>
                                  {menu}
                                  <div
                                    data-testid={`custom-color-input-wrapper-${index}`}
                                    className="flex items-center justify-between gap-3 px-3 py-2 border-t border-gray-100 dark:border-neutral-800"
                                  >
                                    <Input
                                      id={`customColorName-${index}`}
                                      value={customColorName}
                                      onChange={(e) =>
                                        setCustomColorName(e.target.value)
                                      }
                                      onKeyDown={(e) => {
                                        e.stopPropagation();
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          handleAddCustomColor(e);
                                        }
                                      }}
                                      placeholder={t('addCustomColor')}
                                      classNameContainer="flex-1"
                                      className="!bg-transparent !rounded-full !border-gray-200 dark:!border-neutral-700"
                                    />
                                    <Button
                                      variant="outline"
                                      onClick={handleAddCustomColor}
                                      className="!border-0 !bg-transparent !text-primary-base !font-semibold hover:!text-pink-500 !shadow-none !px-1 shrink-0 flex items-center gap-1"
                                    >
                                      <InfoText
                                        text="+"
                                        className="text-base leading-none inline-block mr-1"
                                      />{' '}
                                      {t('add')}
                                    </Button>
                                  </div>
                                </>
                              )}
                              value={
                                watchedColors[index]?.color
                                  ? JSON.stringify(watchedColors[index]?.color)
                                  : undefined
                              }
                              onChange={(val: unknown) => {
                                try {
                                  const parsed = JSON.parse(val as string);
                                  setValue(`colors.${index}.color`, parsed, {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                  });
                                  // Force re-validation of the entire array to clear cross-field duplication errors
                                  setTimeout(() => trigger('colors'), 0);
                                } catch {
                                  // noop
                                }
                              }}
                              data-testid={`color-input-${index}`}
                            />
                          </div>

                          {/* Image upload */}
                          <div
                            data-testid={`color-image-upload-wrapper-${index}`}
                            className="flex flex-col items-center gap-1"
                          >
                            <div
                              className="h-[46px] flex items-center relative"
                              data-testid={`color-image-upload-container-${index}`}
                            >
                              <input
                                type="file"
                                accept="image/*"
                                ref={(el) => {
                                  fileInputRefs.current[index] = el;
                                }}
                                onChange={(e) => handleFileChange(index, e)}
                                className="hidden"
                              />
                              <button
                                type="button"
                                disabled={isUploading}
                                onClick={() =>
                                  fileInputRefs.current[index]?.click()
                                }
                                className={classNames(
                                  'w-[46px] h-[46px] rounded-xl border flex items-center justify-center overflow-hidden transition-all duration-200 relative',
                                  imageErrorMessage
                                    ? '!border-red-500 !bg-red-50 dark:!bg-red-950/20 text-red-500'
                                    : imageUrl
                                      ? 'border-pink-500'
                                      : 'border-light-input-border dark:border-dark-input-border bg-soft-background dark:bg-dark-surface-secondary hover:bg-neutral-100 dark:hover:bg-neutral-800',
                                  isUploading && 'opacity-60 cursor-not-allowed'
                                )}
                                data-testid={`color-image-upload-${index}`}
                              >
                                {isUploading ? (
                                  <LoaderSpin size="small" />
                                ) : imageUrl ? (
                                  <img
                                    src={imageUrl}
                                    alt="Color Preview"
                                    className="object-cover w-full h-full"
                                  />
                                ) : (
                                  <GalleryAdd
                                    size={20}
                                    className="text-neutral-500"
                                  />
                                )}
                              </button>
                            </div>
                            {imageErrorMessage && (
                              <InfoText
                                text={imageErrorMessage}
                                size="xs"
                                className="!text-red-500 dark:!text-red-400 !mt-[-5px] whitespace-nowrap text-center"
                                data-testid={`color-image-error-${index}`}
                              />
                            )}
                          </div>

                          {/* Delete row */}
                          <div
                            className="h-[46px] flex items-center"
                            data-testid={`color-delete-container-${index}`}
                          >
                            <button
                              type="button"
                              onClick={() => removeColor(index)}
                              className="w-10 h-10 flex items-center justify-center bg-red-50 text-red-500 hover:bg-red-100 dark:bg-red-950/20 dark:hover:bg-red-900/40 rounded-full transition-all duration-200"
                              data-testid={`color-delete-${index}`}
                            >
                              <Trash size={20} variant="Outline" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Add new color row */}
                  <button
                    type="button"
                    onClick={() =>
                      appendColor(
                        { color: undefined, image: [] },
                        { shouldFocus: false }
                      )
                    }
                    className="flex items-center gap-1.5 text-base !font-semibold leading-none !text-light-success dark:!text-dark-success hover:opacity-80 transition-opacity"
                    data-testid="add-color-btn"
                  >
                    <AddCircle size={18} variant="Bold" />
                    {t('addNewColor')}
                  </button>
                </div>
              )}

              {/* ── Dynamic Variants Table ─────────────────────── */}
              <VariantsTable
                attributes={attributes}
                currency={currencyCode}
                isCart={productType === 'cart'}
                applyPricingTiers={applyPricingTiers}
                attributesLoading={attributesLoading}
                permutations={memoizedPermutations}
                exchangeRate={EXCHANGE_RATE}
                preferredCurrency={PREFERRED_CURRENCY}
              />
            </div>
          )}

          {/* ── Base Stock and Price (When Variants Disabled) ──── */}
          {!applyVariants && (
            <div
              className="space-y-4 mt-6 animate-fade-in"
              data-testid="base-inventory-section"
            >
              <div
                data-testid="sku-warehouse-fields-wrapper"
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
              >
                {!applyPricingTiers && (
                  <FormField
                    name="basePrice"
                    id="basePrice"
                    type="number"
                    max={999999999}
                    required={true}
                    className="!font-semibold !text-neutral-900 dark:!text-dark-text-secondary"
                    label={t('basePriceLabel')}
                    placeholder={t('basePricePlaceholder')}
                    data-testid="sku-base-price-input"
                  />
                )}
                <FormField
                  name="baseStock"
                  id="baseStock"
                  type="number"
                  max={999999999}
                  required={true}
                  className="!font-semibold !text-neutral-900 dark:!text-dark-text-secondary"
                  label={t('baseStockLabel')}
                  placeholder={t('baseStockPlaceholder')}
                  data-testid="sku-base-stock-input"
                />
              </div>
            </div>
          )}
        </div>
      )}
      <input type="hidden" {...register('colorAttributeId')} />
    </div>
  );
};

export default SkuInventoryForm;
